import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE, getAuthToken } from "@/lib/api";
import { toast } from "sonner";
import { Clock, X, Edit2, Trash2, ChevronRight, Settings, Save, Download, CalendarDays, Check, AlertTriangle, FileText, FileSpreadsheet } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { fmtNL } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";

function AutoStopSettings({ company, onSaved }) {
  const initial = ((company?.settings || {}).auto_stop_hour ?? 17);
  const [hour, setHour] = useState(String(initial).padStart(2, "0"));
  const [saving, setSaving] = useState(false);
  const dirty = Number(hour) !== Number(initial);

  useEffect(() => {
    setHour(String(((company?.settings || {}).auto_stop_hour ?? 17)).padStart(2, "0"));
  }, [company]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/settings", { auto_stop_hour: Number(hour) });
      toast.success("Instelling opgeslagen — geldt voor alle uitvoerders");
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  return (
    <div className="pb-card p-4 mb-6 bg-slate-50 border-slate-900" data-testid="auto-stop-settings">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="p-2 bg-orange-600 text-white rounded shrink-0"><Settings size={18}/></div>
        <div className="flex-1 min-w-[240px]">
          <div className="pb-heading font-black text-base">Automatisch stoppen</div>
          <div className="text-xs text-slate-600 font-semibold mt-0.5">Als een uitvoerder vergeet op STOP te drukken, wordt de timer automatisch afgesloten op dit tijdstip. Geldt voor iedereen in je bedrijf.</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-bold uppercase tracking-widest text-slate-700">Stop-tijd</label>
          <select
            value={hour}
            onChange={(e) => setHour(e.target.value)}
            className="pb-input !min-h-[40px] !w-auto pr-8"
            data-testid="auto-stop-hour-select"
            disabled={saving}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={String(i).padStart(2, "0")}>{String(i).padStart(2, "0")}:00</option>
            ))}
          </select>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="pb-btn-primary !min-h-[40px] flex items-center gap-1 disabled:opacity-50"
            data-testid="btn-save-auto-stop"
          >
            <Save size={14}/> {saving ? "…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PendingConfirmBanner({ isOffice, onChanged }) {
  const [pending, setPending] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState({});
  const [projects, setProjects] = useState({});
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!isOffice) return;
    try {
      const [r, u, p] = await Promise.all([
        api.get("/time-entries/pending-confirm"),
        api.get("/users"),
        api.get("/projects"),
      ]);
      setPending(r.data || []);
      setUsers(Object.fromEntries((u.data || []).map((x) => [x.id, x])));
      setProjects(Object.fromEntries((p.data || []).map((x) => [x.id, x])));
    } catch (err) { console.warn("Openstaande uren laden mislukt", err); }
  }, [isOffice]);
  useEffect(() => { load(); }, [load]);

  if (!isOffice || pending.length === 0) return null;

  const confirmOne = async (e, newStop) => {
    setBusy(true);
    try {
      await api.post(`/time-entries/${e.id}/confirm`, newStop ? { new_stop_at: newStop } : {});
      toast.success(newStop ? "Aangepast & bevestigd" : "Bevestigd");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bevestigen mislukt");
    } finally { setBusy(false); }
  };

  const confirmAll = async () => {
    setBusy(true);
    for (const e of pending) {
      try { await api.post(`/time-entries/${e.id}/confirm`, {}); } catch (err) { console.warn("Bevestigen mislukt", e.id, err); }
    }
    toast.success(`${pending.length} uren bevestigd`);
    await load();
    onChanged && onChanged();
    setBusy(false);
  };

  return (
    <div className="mb-6 rounded border-2 border-orange-500 bg-orange-50 overflow-hidden" data-testid="pending-confirm-banner">
      <div className="p-3 flex items-center gap-3 flex-wrap border-b-2 border-orange-500 bg-orange-100">
        <div className="p-2 bg-orange-600 text-white rounded shrink-0"><AlertTriangle size={18}/></div>
        <div className="flex-1 min-w-[220px]">
          <div className="pb-heading font-black text-base text-orange-900" data-testid="pending-count">{pending.length} uren nog te bevestigen</div>
          <div className="text-xs text-orange-900/80 font-semibold">Deze registraties zijn automatisch gestopt op de ingestelde eindtijd. Bevestig of pas de eindtijd aan voordat je ze accordeert of exporteert.</div>
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="pb-btn-outline !min-h-[38px] !border-orange-600 !text-orange-800" data-testid="btn-toggle-pending">
          {expanded ? "Inklappen" : "Bekijk regels"}
        </button>
        <button onClick={confirmAll} disabled={busy} className="pb-btn-primary flex items-center gap-1 !min-h-[38px]" data-testid="btn-confirm-all">
          <Check size={14}/> Alles bevestigen
        </button>
      </div>
      {expanded && (
        <div className="divide-y-2 divide-orange-200 bg-white">
          {pending.map((e) => {
            const u = users[e.user_id];
            const p = projects[e.project_id];
            const stopTime = e.stop_at ? new Date(e.stop_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "—";
            const dateLbl = e.start_at ? new Date(e.start_at).toLocaleDateString("nl-NL") : "";
            const dur = e.duration_minutes || 0;
            return (
              <div key={e.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`pending-row-${e.id}`}>
                <div className="flex-1 min-w-[240px]">
                  <div className="pb-heading font-black text-sm text-slate-900">
                    {u?.name || "?"} <span className="text-slate-500 font-semibold text-xs">· {p?.name || "geen project"} · {dateLbl}</span>
                  </div>
                  <div className="text-xs text-orange-700 font-bold mt-0.5">
                    Automatisch gestopt om {stopTime} — klopt dat? ({fmtHours(dur)})
                  </div>
                </div>
                <button onClick={() => setEditing(e)} disabled={busy} className="pb-btn-outline !min-h-[36px] flex items-center gap-1 text-xs" data-testid={`btn-edit-pending-${e.id}`}>
                  <Edit2 size={12}/> Eindtijd aanpassen
                </button>
                <button onClick={() => confirmOne(e)} disabled={busy} className="pb-btn-primary !min-h-[36px] flex items-center gap-1 text-xs" data-testid={`btn-confirm-pending-${e.id}`}>
                  <Check size={12}/> Bevestigen
                </button>
              </div>
            );
          })}
        </div>
      )}
      {editing && (
        <EditPendingModal
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={async (newStop) => { await confirmOne(editing, newStop); setEditing(null); }}
        />
      )}
    </div>
  );
}

function EditPendingModal({ entry, onClose, onSave }) {
  const [stopAt, setStopAt] = useState(toLocalDateTimeInput(entry.stop_at));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const s = new Date(entry.start_at);
    const e = new Date(stopAt);
    if (isNaN(e.getTime())) { toast.error("Ongeldige tijd"); return; }
    if (e <= s) { toast.error("Eindtijd moet na starttijd zijn"); return; }
    if ((e - s) / 36e5 > 24) { toast.error("Duur mag niet langer dan 24 uur zijn"); return; }
    setSaving(true);
    try { await onSave(e.toISOString()); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/70" onClick={onClose}>
      <div className="pb-card w-full max-w-md" onClick={(ev) => ev.stopPropagation()} data-testid="edit-pending-modal">
        <div className="bg-orange-600 text-white p-4 flex items-center justify-between">
          <div className="pb-heading text-lg font-black">Eindtijd corrigeren</div>
          <button onClick={onClose} className="text-white"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs font-semibold text-slate-600">
            Start was op <b>{new Date(entry.start_at).toLocaleString("nl-NL")}</b>. Systeem stopte automatisch op {entry.stop_at ? new Date(entry.stop_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "—"}.
          </div>
          <label className="block">
            <span className="text-xs font-black uppercase tracking-widest text-slate-700">Werkelijke eindtijd</span>
            <input type="datetime-local" className="pb-input mt-1" value={stopAt} onChange={(e) => setStopAt(e.target.value)} data-testid="pending-edit-stop"/>
          </label>
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2">
          <button onClick={onClose} className="pb-btn-outline flex-1">Annuleren</button>
          <button onClick={save} disabled={saving} className="pb-btn-primary flex-1" data-testid="btn-save-pending">{saving ? "Bezig…" : "Aanpassen & bevestigen"}</button>
        </div>
      </div>
    </div>
  );
}

function fmtHours(min) {
  const m = min || 0;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return `${h}u ${String(rem).padStart(2, "0")}m`;
}

function fmtHoursDecimal(min) {
  return ((min || 0) / 60).toFixed(1);
}

export default function Uren() {
  const { user, company, refresh: refreshAuth } = useAuth();
  const [summary, setSummary] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const monthFirst = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
  const today = () => new Date().toISOString().slice(0, 10);
  const [exportFrom, setExportFrom] = useState(monthFirst());
  const [exportTo, setExportTo] = useState(today());
  const isOwner = user?.role === "OWNER";
  const isOffice = ["OWNER", "PLANNER"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/time-entries/summary");
      setSummary(r.data);
    } catch { toast.error("Kon uren niet laden"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load projects + users lazily when export modal opens
  useEffect(() => {
    if (!showExport || !isOffice) return;
    (async () => {
      try {
        const [pr, us] = await Promise.all([api.get("/projects"), api.get("/users")]);
        setProjects(pr.data || []);
        setUsers((us.data || []).filter((u) => u.role === "UITVOERDER"));
      } catch (err) { console.warn("Projecten/medewerkers laden mislukt", err); }
    })();
  }, [showExport, isOffice]);

  const runExport = async (format /* 'xlsx' | 'pdf' */, allTime = false) => {
    if (!allTime && exportFrom > exportTo) {
      toast.error("Van-datum kan niet later dan tot-datum zijn");
      return;
    }
    setExporting(true);
    try {
      const token = getAuthToken();
      const parts = [];
      if (!allTime) { parts.push(`from_date=${exportFrom}`); parts.push(`to_date=${exportTo}`); }
      if (filterUser) parts.push(`user_id=${filterUser}`);
      if (filterProject) parts.push(`project_id=${filterProject}`);
      const qs = parts.length ? `?${parts.join("&")}` : "";
      const resp = await fetch(`${API_BASE}/time-entries/export.${format}${qs}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const userLbl = filterUser ? `_${(users.find((u) => u.id === filterUser)?.name || "uitv").replace(/\s+/g, "-")}` : "";
      const projLbl = filterProject ? `_${(projects.find((p) => p.id === filterProject)?.name || "proj").replace(/\s+/g, "-")}` : "";
      const rangeLbl = allTime ? "alles" : `${exportFrom}_tot_${exportTo}`;
      a.href = url;
      a.download = `ploegbord-uren-${rangeLbl}${userLbl}${projLbl}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()}-export gedownload`);
      setShowExport(false);
    } catch { toast.error("Export mislukt"); }
    finally { setExporting(false); }
  };

  if (loading || !summary) return <div className="p-8 font-semibold">Laden…</div>;

  const totals = summary.users.reduce((acc, u) => ({
    week: acc.week + u.week_minutes,
    month: acc.month + u.month_minutes,
    total: acc.total + u.total_minutes,
  }), { week: 0, month: 0, total: 0 });

  const sorted = [...summary.users].sort((a, b) => b.week_minutes - a.week_minutes);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="pb-heading text-3xl font-black">UREN</h1>
          <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Werkuren per uitvoerder (start/stop timer)</div>
        </div>
        {isOffice && (
          <button
            onClick={() => setShowExport(true)}
            className="pb-btn-outline flex items-center gap-2"
            data-testid="btn-open-export-uren"
          >
            <Download size={16}/> Exporteer uren
          </button>
        )}
      </div>

      {showExport && isOffice && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70" onClick={() => setShowExport(false)}>
          <div className="pb-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="export-modal">
            <div className="sticky top-0 bg-slate-900 text-white p-4 flex items-center justify-between">
              <div className="pb-heading text-lg font-black flex items-center gap-2"><Download size={18}/> Uren exporteren</div>
              <button onClick={() => setShowExport(false)} className="text-white"><X size={20}/></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="text-sm font-semibold text-slate-700">Kies de periode voor de urenexport. Alle uren van uitvoerders binnen deze periode komen in het bestand.</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-700">Van</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="pb-input mt-1 flex items-center gap-2 text-left" data-testid="export-from">
                        <CalendarDays size={14}/> {exportFrom ? new Date(exportFrom).toLocaleDateString("nl-NL") : "Kies datum"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[70]" align="start">
                      <Calendar mode="single" weekStartsOn={1} selected={exportFrom ? new Date(exportFrom) : undefined} onSelect={(d) => { if (d) setExportFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`); }} />
                    </PopoverContent>
                  </Popover>
                </label>
                <label className="block">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-700">Tot en met</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="pb-input mt-1 flex items-center gap-2 text-left" data-testid="export-to">
                        <CalendarDays size={14}/> {exportTo ? new Date(exportTo).toLocaleDateString("nl-NL") : "Kies datum"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[70]" align="start">
                      <Calendar mode="single" weekStartsOn={1} selected={exportTo ? new Date(exportTo) : undefined} onSelect={(d) => { if (d) setExportTo(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`); }} />
                    </PopoverContent>
                  </Popover>
                </label>
              </div>
              <div className="flex flex-wrap gap-1.5 text-xs font-bold">
                {[
                  { l: "Deze week", from: (() => { const d = new Date(); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); return d.toISOString().slice(0, 10); })(), to: today() },
                  { l: "Deze maand", from: monthFirst(), to: today() },
                  { l: "Vorige maand", from: (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10); })(), to: (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10); })() },
                  { l: "Dit jaar", from: (() => { const d = new Date(); return `${d.getFullYear()}-01-01`; })(), to: today() },
                ].map((p) => (
                  <button key={p.l} onClick={() => { setExportFrom(p.from); setExportTo(p.to); }} className="border-2 border-slate-900 rounded px-2 py-1 hover:bg-orange-50" data-testid={`preset-${p.l.replace(/\s/g,'-').toLowerCase()}`}>{p.l}</button>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-3 pt-2 border-t-2 border-slate-100">
                <label className="block">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-700">Uitvoerder (optioneel)</span>
                  <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} className="pb-input mt-1" data-testid="export-user-filter">
                    <option value="">Alle uitvoerders</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-700">Project (optioneel)</span>
                  <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="pb-input mt-1" data-testid="export-project-filter">
                    <option value="">Alle projecten</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
              </div>
            </div>
            <div className="p-4 border-t-2 border-slate-900 space-y-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Download voor deze periode:</div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => runExport("xlsx", false)} disabled={exporting} className="pb-btn-outline flex-1 !min-h-[42px] flex items-center justify-center gap-2 text-sm" data-testid="btn-export-xlsx">
                  <FileSpreadsheet size={16}/> Excel (.xlsx)
                </button>
                <button onClick={() => runExport("pdf", false)} disabled={exporting} className="pb-btn-primary flex-1 !min-h-[42px] flex items-center justify-center gap-2 text-sm" data-testid="btn-export-pdf">
                  <FileText size={16}/> PDF met akkoord
                </button>
              </div>
              <button onClick={() => runExport("xlsx", true)} disabled={exporting} className="w-full text-xs font-bold text-slate-600 hover:text-orange-600 underline" data-testid="btn-export-all">Of exporteer alle uren (Excel, hele historie)</button>
            </div>
          </div>
        </div>
      )}

      {isOwner && (
        <AutoStopSettings company={company} onSaved={refreshAuth}/>
      )}
      <PendingConfirmBanner isOffice={isOffice} onChanged={load} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <TotalCard label="Deze week" value={fmtHours(totals.week)} sub={`${fmtHoursDecimal(totals.week)} uur`} testId="total-week"/>
        <TotalCard label="Deze maand" value={fmtHours(totals.month)} sub={`${fmtHoursDecimal(totals.month)} uur`} testId="total-month"/>
        <TotalCard label="Totaal" value={fmtHours(totals.total)} sub={`${fmtHoursDecimal(totals.total)} uur`} testId="total-all"/>
      </div>

      <div className="pb-card overflow-hidden">
        <div className="p-4 border-b-2 border-slate-900 bg-slate-50">
          <div className="pb-heading text-lg font-black">Per uitvoerder</div>
        </div>
        <div className="divide-y-2 divide-slate-200">
          {sorted.length === 0 && <div className="p-6 text-center text-slate-500">Geen uitvoerders gevonden.</div>}
          {sorted.map((u) => (
            <button
              key={u.user_id}
              onClick={() => setSelectedUser(u)}
              className="w-full flex items-center gap-3 p-4 hover:bg-orange-50 text-left"
              data-testid={`user-row-${u.user_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white font-black flex items-center justify-center flex-shrink-0">
                {(u.user_name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{u.user_name}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{u.function || "Uitvoerder"}</div>
              </div>
              {u.active_entry && (
                <div className="text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-orange-600 text-white flex items-center gap-1">
                  <Clock size={12}/> Loopt nu
                </div>
              )}
              <div className="grid grid-cols-3 gap-3 text-right">
                <MiniStat label="Week" value={fmtHours(u.week_minutes)}/>
                <MiniStat label="Maand" value={fmtHours(u.month_minutes)}/>
                <MiniStat label="Totaal" value={fmtHours(u.total_minutes)}/>
              </div>
              <ChevronRight size={20} className="text-slate-400 ml-2"/>
            </button>
          ))}
        </div>
      </div>

      {selectedUser && <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} onChanged={load} />}
    </div>
  );
}

function TotalCard({ label, value, sub, testId }) {
  return (
    <div className="pb-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-700 mb-2"><Clock size={16}/><span className="text-xs font-bold uppercase tracking-widest">{label}</span></div>
      <div className="pb-heading text-3xl font-black tabular-nums">{value}</div>
      {sub && <div className="text-xs font-semibold text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="pb-heading font-black tabular-nums text-base">{value}</div>
    </div>
  );
}

function UserDetailModal({ user: initialUser, onClose, onChanged }) {
  const [user, setUser] = useState(initialUser);
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p, sm] = await Promise.all([
        api.get("/time-entries", { params: { user_id: user.user_id } }),
        api.get("/projects"),
        api.get("/time-entries/summary"),
      ]);
      setEntries(e.data);
      setProjects(Object.fromEntries(p.data.map((x) => [x.id, x])));
      const fresh = sm.data.users.find((x) => x.user_id === user.user_id);
      if (fresh) setUser(fresh);
    } catch { toast.error("Kon uren niet laden"); }
    finally { setLoading(false); }
  }, [user.user_id]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("Deze regel verwijderen?")) return;
    try {
      await api.delete(`/time-entries/${id}`);
      toast.success("Verwijderd");
      await load();
      onChanged();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  // Group by date (in NL, based on start_at)
  const byDate = {};
  entries.forEach((e) => {
    const d = new Date(e.start_at).toLocaleDateString("nl-NL");
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(e);
  });
  const dateEntries = Object.entries(byDate);

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-slate-900/50">
      <div className="pb-card w-full max-w-3xl max-h-[95vh] overflow-y-auto pb-scrollbar" data-testid="user-detail-modal">
        <div className="sticky top-0 bg-slate-900 text-white p-4 flex items-center justify-between">
          <div>
            <div className="pb-heading text-xl font-black">{user.user_name}</div>
            <div className="text-xs uppercase tracking-widest opacity-80">{user.function}</div>
          </div>
          <button onClick={onClose} className="text-white" data-testid="btn-close-user-modal"><X size={22}/></button>
        </div>

        <div className="grid grid-cols-3 gap-2 p-3 border-b-2 border-slate-900 bg-slate-50">
          <MiniStat label="Deze week" value={fmtHours(user.week_minutes)}/>
          <MiniStat label="Deze maand" value={fmtHours(user.month_minutes)}/>
          <MiniStat label="Totaal" value={fmtHours(user.total_minutes)}/>
        </div>

        <div className="p-4">
          {loading && <div className="text-sm text-slate-500">Laden…</div>}
          {!loading && dateEntries.length === 0 && <div className="text-sm text-slate-500">Geen uren geregistreerd.</div>}
          {!loading && dateEntries.map(([d, list]) => {
            const dayTotal = list.reduce((s, e) => s + (e.duration_minutes || 0), 0);
            return (
              <div key={d} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="pb-heading font-black text-slate-900">{d}</div>
                  <div className="text-sm font-bold text-orange-600 tabular-nums">{fmtHours(dayTotal)}</div>
                </div>
                <div className="space-y-2">
                  {list.map((e) => {
                    const p = projects[e.project_id];
                    const isPending = e.pending_confirm;
                    return (
                      <div key={e.id} className={`border-2 rounded p-3 flex items-center gap-3 ${isPending ? "border-orange-500 bg-orange-50" : "border-slate-900"}`} data-testid={`time-entry-${e.id}`}>
                        <div className="w-1 h-full rounded" style={{ background: p?.color || "#EA580C" }}/>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold truncate">{p?.name || "—"}</div>
                          <div className="text-xs text-slate-600 font-semibold">
                            {new Date(e.start_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                            {" – "}
                            {e.stop_at ? new Date(e.stop_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : <span className="text-orange-600 font-bold">LOOPT</span>}
                            {e.edited && <span className="ml-1 text-blue-600">(aangepast)</span>}
                            {e.confirmed_by && !isPending && <span className="ml-1 text-emerald-700">(bevestigd)</span>}
                          </div>
                          {e.stop_at && (e.break_minutes > 0) && (
                            <div className="text-[11px] font-bold text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                              <span>Bruto <span className="tabular-nums text-slate-900">{fmtHours(e.duration_minutes)}</span></span>
                              <span>· Pauze <span className="tabular-nums text-orange-700">{e.break_minutes}m</span></span>
                              <span>· Netto <span className="tabular-nums text-emerald-700">{fmtHours(Math.max(0, (e.duration_minutes || 0) - (e.break_minutes || 0)))}</span></span>
                            </div>
                          )}
                          {isPending && (
                            <div className="mt-1 text-xs text-orange-700 font-bold">
                              Automatisch gestopt om {new Date(e.stop_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })} — klopt dat?
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="pb-heading font-black tabular-nums text-lg" title={e.break_minutes > 0 ? "Netto (na pauze)" : "Bruto"}>
                            {e.stop_at ? fmtHours(Math.max(0, (e.duration_minutes || 0) - (e.break_minutes || 0))) : "—"}
                          </div>
                          {e.stop_at && e.break_minutes > 0 && (
                            <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">netto</div>
                          )}
                        </div>
                        {isPending && (
                          <button
                            onClick={async () => {
                              try { await api.post(`/time-entries/${e.id}/confirm`, {}); toast.success("Bevestigd"); await load(); onChanged(); }
                              catch (err) { toast.error(err?.response?.data?.detail || "Bevestigen mislukt"); }
                            }}
                            className="p-2 border-2 border-emerald-600 text-emerald-700 rounded hover:bg-emerald-50"
                            data-testid={`btn-confirm-entry-${e.id}`}
                            title="Bevestigen"
                          >
                            <Check size={14}/>
                          </button>
                        )}
                        {e.stop_at && (
                          <>
                            <button onClick={() => setEditing(e)} className="p-2 border-2 border-slate-900 rounded hover:bg-orange-50" data-testid={`btn-edit-entry-${e.id}`}>
                              <Edit2 size={14}/>
                            </button>
                            <button onClick={() => remove(e.id)} className="p-2 border-2 border-red-600 text-red-600 rounded hover:bg-red-50" data-testid={`btn-delete-entry-${e.id}`}>
                              <Trash2 size={14}/>
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {editing && <EditEntryModal entry={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); onChanged(); }}/>}
      </div>
    </div>
  );
}

function toLocalDateTimeInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function EditEntryModal({ entry, onClose, onSaved }) {
  const [startAt, setStartAt] = useState(toLocalDateTimeInput(entry.start_at));
  const [stopAt, setStopAt] = useState(toLocalDateTimeInput(entry.stop_at));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const s = new Date(startAt);
    const e = new Date(stopAt);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
      toast.error("Ongeldige datum/tijd");
      return;
    }
    if (e <= s) {
      toast.error("Starttijd moet vóór stoptijd liggen");
      return;
    }
    if ((e - s) / 36e5 > 24) {
      toast.error("Duur mag niet langer dan 24 uur zijn");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/time-entries/${entry.id}`, {
        start_at: s.toISOString(),
        stop_at: e.toISOString(),
      });
      toast.success("Bijgewerkt");
      await onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Opslaan mislukt";
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70">
      <div className="pb-card w-full max-w-md" data-testid="edit-entry-modal">
        <div className="sticky top-0 bg-slate-900 text-white p-4 flex items-center justify-between">
          <div className="pb-heading text-xl font-black">Uren aanpassen</div>
          <button onClick={onClose} className="text-white"><X size={22}/></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-700">Start</span>
            <input type="datetime-local" className="pb-input mt-1" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="edit-start-at"/>
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-700">Stop</span>
            <input type="datetime-local" className="pb-input mt-1" value={stopAt} onChange={(e) => setStopAt(e.target.value)} data-testid="edit-stop-at"/>
          </label>
        </div>
        <div className="p-4 border-t-2 border-slate-900 flex gap-2">
          <button onClick={onClose} className="pb-btn-outline flex-1">Annuleren</button>
          <button onClick={save} disabled={saving} className="pb-btn-primary flex-1" data-testid="btn-save-entry">{saving ? "Opslaan…" : "Opslaan"}</button>
        </div>
      </div>
    </div>
  );
}
